import React, { useState, useEffect, useContext } from "react";
import cogoToast from "cogo-toast";
import { Card, CardBody, CardHeader, Table, Input, Button, Row, Col, Label} from "reactstrap";
import { listStaffPayslipsApi, getDownloadablePayslip } from "../../services/Api";
import { SUCCESS_RESULT, BASE_URL, ENDPOINTS } from "../../Constants";
import { FaFileExport, FaSearch, FaExternalLinkAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import { PulseLoader } from "react-spinners";
import AppContext from "../../AppContext";
import { getLoggedInStaffAccountId, hasLoggedInStaffProfile } from "../../services/Utils";
import * as XLSX from 'xlsx';

const StaffPayslips = () => {
  const global = useContext(AppContext);
  const [is_processing, setProcessing] = useState(false);
  const [payslips, setPayslips] = useState([]);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleAmounts, setVisibleAmounts] = useState(new Set());

  useEffect(() => {
    const loggedInUser = global.get_logged_in_user();
    if (loggedInUser) {
      setUser(loggedInUser);
    }
  }, [global]);

  useEffect(() => {
    if (user && getLoggedInStaffAccountId(user)) {
      fetchPayslips();
    }
  }, [user]);

  const fetchPayslips = () => {
    const staffId = getLoggedInStaffAccountId(user);
    if (!staffId) {
      cogoToast.error("Staff ID not found. Please ensure you have staff information.", {
        hideAfter: 5,
      });
      return;
    }

    setProcessing(true);
    listStaffPayslipsApi({
      staff_id: staffId,
    })
      .then((res) => {
        setProcessing(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setPayslips(response.data || []);
          } else {
            cogoToast.error(response.message || "Failed to load payslips", {
              hideAfter: 5,
            });
            setPayslips([]);
          }
        } else {
          cogoToast.error(res.message || "Failed to load payslips", {
            hideAfter: 5,
          });
          setPayslips([]);
        }
      })
      .catch((err) => {
        setProcessing(false);
        const errorMessage =
          err?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
        setPayslips([]);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return `$${Number(amount).toFixed(2)}`;
  };

  const toggleAmountVisibility = (payslipId) => {
    setVisibleAmounts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(payslipId)) {
        newSet.delete(payslipId);
      } else {
        newSet.add(payslipId);
      }
      return newSet;
    });
  };

  const getPayslipId = (payslip) => {
    return payslip.id || `${payslip.year}-${payslip.month}-${payslip.track_id || ''}`;
  };

  const handleOpenPayslip = (payslip) => {
    const staffId = getLoggedInStaffAccountId(user);
    
    if (!staffId) {
      cogoToast.error("Staff ID not found", { hideAfter: 3 });
      return;
    }

    // Prepare data for payslip API (same structure as GeneratePayslips)
    const payslipData = {
      staff_id: staffId,
      month: payslip.month,
      year: payslip.year,
    };

    // Add track_id if available
    if (payslip.track_id) {
      payslipData.track_id = payslip.track_id;
    }

    // Show loading toast
    const loadingToast = cogoToast.loading("Loading payslip...", { hideAfter: 0 });

    // Fetch payslip PDF using the same method as GeneratePayslips
    getDownloadablePayslip(ENDPOINTS.GET_PAYSLIP, payslipData)
      .then((pdfBlob) => {
        loadingToast.hide();
        
        try {
          // Create object URL from blob
          const pdfUrl = URL.createObjectURL(pdfBlob);
          
          // Open PDF in new tab
          const newWindow = window.open(pdfUrl, '_blank');
          
          // If popup was blocked, fall back to download
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Fallback to download if popup blocked
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `Payslip-${payslip.year}-${payslip.month}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            cogoToast.info("Popup blocked. Payslip downloaded instead.", { hideAfter: 3 });
          } else {
            cogoToast.success("Payslip opened in new tab", { hideAfter: 3 });
          }
          
          // Clean up URL after a delay to allow the tab to load
          setTimeout(() => {
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        } catch (error) {
          console.error('Error opening payslip:', error);
          cogoToast.error("Failed to open payslip. Please try again.", { hideAfter: 5 });
        }
      })
      .catch((err) => {
        loadingToast.hide();
        const errorMessage =
          err?.response?.data?.message || "Failed to load payslip. Please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const handleExport = () => {
    if (!payslips || payslips.length === 0) {
      cogoToast.error("No data available to export", {
        hideAfter: 3,
      });
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = payslips.map((payslip, index) => {
        return {
          '#': index + 1,
          'Period': payslip.period || payslip.month_name || '',
          'Year': payslip.year || '',
          'Month': payslip.month || '',
          'Net Pay': payslip.net_pay || 0,
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // #
        { wch: 20 },  // Period
        { wch: 8 },   // Year
        { wch: 8 },   // Month
        { wch: 15 },  // Net Pay
      ];

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Payslips');

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        compression: true
      });

      // Create blob and download
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payslips-${window.h?.get_half_formattedDate?.() || new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      cogoToast.success("Excel file exported successfully", {
        hideAfter: 3,
      });
    } catch (error) {
      console.error('Excel export error:', error);
      cogoToast.error("Failed to export Excel file. Please try again.", {
        hideAfter: 5,
      });
    }
  };

  // Filter payslips based on search term
  const filteredPayslips = payslips.filter((payslip) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const searchableFields = [
      payslip.period,
      payslip.month_name,
      payslip.year,
      payslip.month,
      payslip.net_pay,
    ];
    return searchableFields.some((field) =>
      field && String(field).toLowerCase().includes(searchLower)
    );
  });

  // Generate month options for display only
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  if (!hasLoggedInStaffProfile(user)) {
    return (
      <div style={{ padding: "20px" }}>
        <div className="text-center py-5">
          <h3>Access Restricted</h3>
          <p className="text-muted">
            You need to have staff information to view payslips.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div className="page-header d-print-none mb-4">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">My Payslips</h2>
              <div className="text-muted mt-1">
                View and download your payslip history
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xl">
        <Card className="shadow-sm">
          <CardHeader>
            <Row>
              <Col md={10}>
                <Label for="search">Search</Label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    id="search"
                    placeholder="Search payslips..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </Col>
              <Col md={2} className="text-end">
                <Label>&nbsp;</Label>
                <div>
                  {filteredPayslips.length > 0 && (
                    <Button
                      color="success"
                      onClick={handleExport}
                      style={{
                        background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        border: "none",
                        borderRadius: "8px",
                        fontWeight: "600",
                      }}
                    >
                      <FaFileExport style={{ marginRight: "8px" }} />
                      Export
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {is_processing ? (
              <div className="text-center py-5">
                <PulseLoader color="#4E6ECF" loading={true} size={15} />
                <p className="mt-3 text-muted">Loading payslips...</p>
              </div>
            ) : filteredPayslips.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">
                  {searchTerm
                    ? "No payslips found matching your search"
                    : "No payslips found"}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover striped bordered>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Period</th>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Net Pay</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayslips.map((payslip, index) => (
                      <tr 
                        key={payslip.id || index}
                        onClick={() => handleOpenPayslip(payslip)}
                        style={{
                          cursor: 'pointer'
                        }}
                      >
                        <td>{index + 1}</td>
                        <td>
                          <strong>{payslip.period || payslip.month_name || "N/A"}</strong>
                        </td>
                        <td>{payslip.year || "N/A"}</td>
                        <td>
                          {payslip.month
                            ? monthOptions.find((m) => m.value === payslip.month)?.label ||
                              payslip.month
                            : "N/A"}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <strong style={{ color: "#28a745" }}>
                              {visibleAmounts.has(getPayslipId(payslip)) 
                                ? formatCurrency(payslip.net_pay) 
                                : "••••••"}
                            </strong>
                            <Button
                              color="link"
                              size="sm"
                              onClick={() => toggleAmountVisibility(getPayslipId(payslip))}
                              style={{
                                padding: '2px 4px',
                                minWidth: 'auto',
                                color: '#495057'
                              }}
                              title={visibleAmounts.has(getPayslipId(payslip)) ? "Hide amount" : "Show amount"}
                            >
                              {visibleAmounts.has(getPayslipId(payslip)) ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleOpenPayslip(payslip)}
                          >
                            <FaExternalLinkAlt style={{ marginRight: "4px" }} />
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="mt-3 text-muted">
                  Showing <strong>{filteredPayslips.length}</strong> of{" "}
                  <strong>{payslips.length}</strong> payslip(s)
                  {searchTerm && (
                    <span className="ms-2 text-primary">
                      (filtered by "{searchTerm}")
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default StaffPayslips;
